import CompanySwitcherModal from "@/components/CompanySwitcherModal";
import { useTheme } from "@/hooks/useTheme";
import { checkDomainMatch, cn, getContrastTextColor, getRoleDisplayName } from "@/lib/utils";
import { useTeamMemberStore } from "@/stores/teamMemberStore";
import { useUserStore } from "@/stores/userStore";
import { Button } from "antd";
import { RefreshCw, X } from "lucide-react";
import { useState } from "react";
import UserMenu from "./UserMenu";

interface SideBarProps {
  showSidePanel: boolean;
  toggleSidePanel: (state: boolean) => void;
}

const SideBar = ({ showSidePanel, toggleSidePanel }: SideBarProps) => {
  const role = useUserStore((state) => state.getRole());
  const user = useUserStore((state) => state.user);
  const currentCompany = useUserStore((state) => state.currentCompany);
  const roleDisplayName = getRoleDisplayName(role);
  const [showCompanySwitcher, setShowCompanySwitcher] = useState(false);
  const { theme } = useTheme();
  const sidebarContrastTextColor = getContrastTextColor(theme.secondaryColor);
  const primaryContrastTextColor = getContrastTextColor(theme.primaryColor);
  const { teamMembers } = useTeamMemberStore();

  // Find the manager from team members
  const manager = teamMembers.find((member) => member.role === "Manager" || member.role === "Project Manager");
  const assignedContact = manager || (teamMembers.length > 0 ? teamMembers[0] : null);

  // Filter companies based on domain if user is a client (User role)
  // When testing mode is enabled, show all companies without domain filtering
  const isClient = user?.role === "User";
  const isTestingMode = import.meta.env.VITE_ENABLE_COMPANY_SWITCHER_TESTING === "true";
  const matchingCompanies =
    isClient && !isTestingMode
      ? user?.companies?.filter((c) => c.domain && checkDomainMatch(window.location.hostname, c.domain)) || []
      : user?.companies || [];

  const hasMultipleCompanies = matchingCompanies.length > 1 || isTestingMode;

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 shrink-0",
        "transition-[transform,margin] duration-300 ease-in-out",
        "lg:relative lg:translate-x-0",
        showSidePanel ? "translate-x-0 lg:ml-0" : "-translate-x-full lg:translate-x-0 lg:-ml-64",
      )}
      style={{ backgroundColor: theme.secondaryColor }}
    >
      <div className="flex h-full min-h-0 flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center justify-center px-6 relative">
          {theme.logo ? (
            <img src={theme.logo} alt="Logo" className="h-10 w-auto" />
          ) : (
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: theme.primaryColor }}
              >
                <span className="font-bold text-lg" style={{ color: primaryContrastTextColor }}>
                  C
                </span>
              </div>
              <div>
                <div className="font-semibold" style={{ color: sidebarContrastTextColor }}>
                  Client Center
                </div>
                <div className="text-xs font-medium" style={{ color: sidebarContrastTextColor, opacity: 0.85 }}>
                  {roleDisplayName.toUpperCase()} PORTAL
                </div>
              </div>
            </div>
          )}
          <Button
            type="text"
            className="hover:bg-white/10 lg:hidden absolute -right-3"
            style={{ color: getContrastTextColor(theme.secondaryColor) }}
            onClick={() => toggleSidePanel(false)}
            icon={<X className="h-5 w-5" />}
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-6">
          <UserMenu />
        </nav>

        {user?.role === "User" && assignedContact && (
          <div className="px-6 py-4 border-b border-white/10">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors group">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-xs truncate mb-1" style={{ color: sidebarContrastTextColor }}>
                    ASSIGNED TEAM MEMBER:
                  </div>
                  <div className="text-xs font-medium truncate" style={{ color: sidebarContrastTextColor }}>
                    {assignedContact.firstName} {assignedContact.lastName}
                  </div>
                  <div className="text-xs truncate" style={{ color: sidebarContrastTextColor }}>
                    {`(${assignedContact.role || "—"})`}
                  </div>
                  <div className="text-xs truncate" style={{ color: sidebarContrastTextColor }}>
                    {assignedContact.email || "—"}
                  </div>
                  <div className="text-xs truncate" style={{ color: sidebarContrastTextColor }}>
                    {assignedContact.phone || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {user?.role === "User" && !assignedContact && (
          <div className="px-6 py-4 border-b border-white/10">
            <div className="flex-1 min-w-0">
              <div className="p-3 rounded-lg bg-white/5">
                <div className="font-medium text-xs truncate mb-1" style={{ color: sidebarContrastTextColor }}>
                  ASSIGNED TEAM MEMBER:
                </div>
                <div className="text-xs truncate" style={{ color: sidebarContrastTextColor }}>
                  No team member assigned
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Company Switcher - Only show if user has multiple companies and is not Finance role */}
        {user?.role !== "Super" && user?.role !== "Finance" && hasMultipleCompanies && (
          <div className="px-6 py-4 border-b border-white/10">
            <div
              className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors group"
              onClick={() => setShowCompanySwitcher(true)}
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs mb-1" style={{ color: sidebarContrastTextColor }}>
                  CURRENT COMPANY
                </div>
                <div className="font-medium text-sm truncate" style={{ color: sidebarContrastTextColor }}>
                  {currentCompany?.companyName || "Select Company"}
                </div>
                {currentCompany?.domain && (
                  <div className="text-xs truncate" style={{ color: sidebarContrastTextColor }}>
                    {currentCompany.domain}
                  </div>
                )}
              </div>
              <div className="ml-2 flex items-center gap-2">
                <RefreshCw className="h-4 w-4" style={{ color: sidebarContrastTextColor }} />
              </div>
            </div>
          </div>
        )}

        {/* Company Switcher Modal */}
        <CompanySwitcherModal open={showCompanySwitcher} onClose={() => setShowCompanySwitcher(false)} />
      </div>
    </aside>
  );
};

export default SideBar;
